import React, { useMemo } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import 'dayjs/locale/ko';
import { Unstable_Grid2 as Grid } from '@mui/material';
import {
  Alert,
  Box,
  Button as MuiButton,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { AuthButton, Checkbox, Combo, FRadio } from '../../../Components/MUIs';
import { BusinessGridCommon } from '../../../Components/BusinessGridCommon';
import PopBasicEditor from './POP_BASIC_Editor';
import {
  GetColumnDefs1,
  PopBasicDisplayLayerFilterItems,
  PopBasicTypeFilterItems,
  PopBasicUseYnFilterItems,
} from './POP_BASIC_ColumnDefs';
import {
  POP_BASIC_DISPLAY_LAYER,
  POP_BASIC_DISPLAY_LAYER_OPTIONS,
  POP_BASIC_LINK_URL_LAYERS,
  POP_BASIC_TARGET_TYPE_OPTIONS,
  POP_BASIC_TYPE_OPTIONS,
} from './POP_BASIC_Schema';

const WRITE_SECTION_SX = {
  p: { xs: 2, md: 2.5 },
  borderRadius: '12px',
  bgcolor: '#fff',
  border: '1px solid #e8eaef',
};

const WRITE_SECTION_LABEL_SX = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: '#64748b',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  mb: 1.5,
};

const FIELD_GROUP_SX = {
  p: 1.25,
  borderRadius: '8px',
  bgcolor: '#f8fafc',
  border: '1px solid #eef1f6',
};

const FIELD_LABEL_SX = {
  display: 'block',
  color: '#64748b',
  mb: 0.5,
  fontWeight: 600,
  fontSize: '0.75rem',
  lineHeight: 1.2,
};

const INLINE_FIELD_CAPTION_SX = {
  display: 'block',
  color: '#94a3b8',
  mb: 0.25,
  fontSize: '0.6875rem',
  fontWeight: 500,
};

const OPTION_CHECKBOX_SX = {
  m: 0,
  mr: 0.5,
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
  '& .MuiCheckbox-root': { py: 0.25 },
};

/** 노출 시작·종료 DateTimePicker 공통 props (Popper 흔들림 방지) */
const COMPACT_DISP_DT_PROPS = {
  format: 'YYYY-MM-DD HH:mm',
  views: ['year', 'month', 'day', 'hours', 'minutes'],
  desktopModeMediaQuery: '@media (min-width: 0px)',
  sx: { width: 210, maxWidth: '100%' },
  slotProps: {
    textField: {
      size: 'small',
      sx: {
        '& .MuiInputBase-root': {
          height: 34,
          fontSize: '0.8125rem',
        },
        '& .MuiInputBase-input': {
          py: 0.5,
          px: 1,
        },
      },
    },
    popper: {
      placement: 'bottom-start',
      modifiers: [
        { name: 'flip', enabled: false },
        { name: 'offset', options: { offset: [0, 4] } },
      ],
      sx: { zIndex: 1600 },
    },
  },
};

function WriteSection({ title, children, sx = {} }) {
  return (
    <Box sx={{ ...WRITE_SECTION_SX, ...sx }}>
      {title ? (
        <Typography component="h3" sx={WRITE_SECTION_LABEL_SX}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function FieldGroup({ label, children, sx = {} }) {
  return (
    <Box sx={{ ...FIELD_GROUP_SX, ...sx }}>
      {label ? (
        <Typography component="div" sx={FIELD_LABEL_SX}>
          {label}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function POP_BASIC(props) {
  const ColumnDefs1 = GetColumnDefs1();
  const Form = props.Form ?? {};
  const IsDetail = Boolean(Form.popId);
  const IsForceConfirm = Form.popType === 'FORCE_CONFIRM';
  const IsForceCloseChecked = Form.isForceClose === 'Y';
  const IsHideTodayChecked = Form.isHideToday === 'Y';
  const UseYnChecked = Form.useYn !== 'N';
  const IsAdmin = props.userInfo?.authCd === 'ADMIN';
  const ShowCompanySelect = IsAdmin && Form.targetType === 'COMPANY';
  const ShowLinkUrl = POP_BASIC_LINK_URL_LAYERS.includes(Form.displayLayer);

  const dispStartValue = useMemo(
    () => (Form.dispStartDt ? dayjs(Form.dispStartDt) : null),
    [Form.dispStartDt]
  );
  const dispEndValue = useMemo(
    () => (Form.dispEndDt ? dayjs(Form.dispEndDt) : null),
    [Form.dispEndDt]
  );
  const dispEndMinDate = useMemo(
    () => (Form.dispStartDt ? dayjs(Form.dispStartDt) : undefined),
    [Form.dispStartDt]
  );

  const Conditions = [
    {
      type: 'combo',
      name: '팝업 유형',
      items: PopBasicTypeFilterItems,
      value: props.Filters?.popType ?? '',
      onChange: props.HandlePopTypeFilterChange,
    },
    {
      type: 'combo',
      name: '노출 형태',
      items: PopBasicDisplayLayerFilterItems,
      value: props.Filters?.displayLayer ?? '',
      onChange: props.HandleDisplayLayerFilterChange,
    },
    {
      type: 'combo',
      name: '사용여부',
      items: PopBasicUseYnFilterItems,
      value: props.Filters?.useYn ?? '',
      onChange: props.HandleUseYnFilterChange,
    },
  ];

  return (
    <>
      <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
        {!props.IsWritePage && (
          <Grid xs={4} sm={8} md={12}>
            <AuthButton
              menuId="PopupManagement"
              searchYn="Y"
              searchBox="Y"
              addYn="Y"
              saveYn="N"
              deleteYn="Y"
              printYn="N"
              placeholder="제목/내용 검색"
              searchClick={(text) => props.SearchLogic1?.(text)}
              addClick={props.AddLogic1}
              deleteClick={props.DeleteSelectedLogic1}
              conditions={Conditions}
              grids={
                <BusinessGridCommon
                  getRowId={(params) =>
                    String(params.data?.popId ?? params.data?.no ?? '')
                  }
                  gridRef={props.GridRef1}
                  rowData={props.RowData1}
                  columnDefs={ColumnDefs1}
                  rowSelection="multiple"
                  addClick1={props.AddLogic1}
                  deleteClick1={props.DeleteSelectedLogic1}
                  setSelectedRowData={props.SetSelectedRowData1}
                  setChangedRowData={props.SetChangedRowData1}
                  refreshClick1={() => props.RefreshLogic1?.()}
                  onSelectionChanged={() => props.OnSelectionChanged?.()}
                  onCellClicked={(e) => {
                    const field =
                      e?.column?.getColDef?.()?.field ?? e?.colDef?.field;
                    const isCheckboxClick = e?.event?.target?.type === 'checkbox';
                    if (field === 'selectRow' || isCheckboxClick) return;
                    props.HandleRowClick1?.(e?.data);
                  }}
                />
              }
            />
          </Grid>
        )}

        {props.IsWritePage && (
          <Grid xs={4} sm={8} md={12}>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                border: '1px solid #e8eaef',
                bgcolor: '#f4f6f9',
                overflow: 'visible',
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.75,
                  borderBottom: '1px solid #e8eaef',
                  bgcolor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <IconButton size="small" onClick={props.CloseWritePage} aria-label="목록으로">
                  <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#0f172a' }}>
                    {IsDetail ? '팝업 수정' : '팝업 등록'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    유형·노출 조건을 설정한 뒤 제목과 내용을 작성하세요.
                  </Typography>
                </Box>
                <MuiButton
                  variant="contained"
                  disableElevation
                  onClick={props.SaveLogic1}
                  sx={{ borderRadius: '8px', px: 2.5, textTransform: 'none' }}
                >
                  저장
                </MuiButton>
              </Box>

              <Stack
                spacing={2}
                sx={{ px: { xs: 2, md: 3 }, py: 2.5, boxSizing: 'border-box' }}
              >
                {/* 1. 노출 조건 */}
                <WriteSection title="노출 조건" sx={{ '& h3': { mb: 1 } }}>
                  <Stack spacing={1.25}>
                    <FieldGroup label="팝업 유형">
                      <FormControl size="small" sx={{ width: { xs: '100%', sm: 280 } }}>
                        <Select
                          value={Form.popType ?? 'GENERAL'}
                          onChange={(e) => props.HandlePopTypeChange?.(e.target.value)}
                        >
                          {POP_BASIC_TYPE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FieldGroup>

                    <Grid container spacing={1.25}>
                      <Grid xs={12} md={IsAdmin ? 6 : 12}>
                        <FieldGroup label="노출 형태" sx={{ height: '100%' }}>
                          <FRadio
                            value={Form.displayLayer ?? POP_BASIC_DISPLAY_LAYER}
                            onChange={(value) =>
                              props.SetForm((prev) => ({ ...prev, displayLayer: value }))
                            }
                            items={POP_BASIC_DISPLAY_LAYER_OPTIONS.map((o) => ({
                              value: o.value,
                              name: o.label,
                              disabled: IsForceConfirm && o.value !== 'MODAL',
                            }))}
                          />
                          {IsForceConfirm ? (
                            <Alert severity="info" sx={{ mt: 0.75, py: 0, fontSize: '0.75rem' }}>
                              강제 동의형은 중앙 모달만 가능합니다.
                            </Alert>
                          ) : null}
                        </FieldGroup>
                      </Grid>

                      {IsAdmin ? (
                        <Grid xs={12} md={6}>
                          <FieldGroup label="노출 대상" sx={{ height: '100%' }}>
                            <FRadio
                              value={Form.targetType ?? 'ALL'}
                              onChange={(value) =>
                                props.SetForm((prev) => ({
                                  ...prev,
                                  targetType: value,
                                  targetIndCds: [],
                                  targetCoCds: value === 'COMPANY' ? prev.targetCoCds : [],
                                }))
                              }
                              items={POP_BASIC_TARGET_TYPE_OPTIONS.map((o) => ({
                                value: o.value,
                                name: o.label,
                              }))}
                            />
                            {ShowCompanySelect ? (
                              <Box sx={{ mt: 1 }}>
                                <Combo
                                  fullWidth
                                  size="small"
                                  multiple
                                  name="노출 회사"
                                  items={props.CompanyOptions ?? []}
                                  value={Form.targetCoCds ?? []}
                                  onChange={(value) =>
                                    props.SetForm((prev) => ({
                                      ...prev,
                                      targetCoCds: Array.isArray(value) ? value : [],
                                    }))
                                  }
                                  MenuProps={{
                                    PaperProps: {
                                      style: { maxHeight: 320, overflowY: 'auto' },
                                    },
                                  }}
                                />
                              </Box>
                            ) : null}
                          </FieldGroup>
                        </Grid>
                      ) : null}
                    </Grid>

                    <FieldGroup label="노출 기간 · 우선순위">
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'flex-end' }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="ko"
                        >
                          <Stack
                            direction="row"
                            spacing={1.25}
                            flexWrap="wrap"
                            useFlexGap
                            alignItems="flex-end"
                          >
                            <Box sx={{ flexShrink: 0 }}>
                              <Typography component="span" sx={INLINE_FIELD_CAPTION_SX}>
                                시작
                              </Typography>
                              <MuiDateTimePicker
                                {...COMPACT_DISP_DT_PROPS}
                                value={dispStartValue}
                                onChange={(v) =>
                                  props.SetForm((prev) => ({
                                    ...prev,
                                    dispStartDt:
                                      v && dayjs(v).isValid() ? dayjs(v).toDate() : null,
                                  }))
                                }
                              />
                            </Box>
                            <Box sx={{ flexShrink: 0 }}>
                              <Typography component="span" sx={INLINE_FIELD_CAPTION_SX}>
                                종료
                              </Typography>
                              <MuiDateTimePicker
                                {...COMPACT_DISP_DT_PROPS}
                                value={dispEndValue}
                                minDate={dispEndMinDate}
                                onChange={(v) =>
                                  props.SetForm((prev) => ({
                                    ...prev,
                                    dispEndDt:
                                      v && dayjs(v).isValid() ? dayjs(v).toDate() : null,
                                  }))
                                }
                              />
                            </Box>
                          </Stack>
                        </LocalizationProvider>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-end"
                          sx={{ flexShrink: 0 }}
                        >
                          <TextField
                            label="우선순위"
                            type="number"
                            size="small"
                            sx={{ width: { xs: '100%', sm: 88 } }}
                            inputProps={{ min: 1, max: 999 }}
                            value={Form.dispPriority ?? 1}
                            onChange={(e) =>
                              props.SetForm((prev) => ({
                                ...prev,
                                dispPriority: e.target.value,
                              }))
                            }
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              pb: 1.25,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            작을수록 먼저 (기본 1)
                          </Typography>
                        </Stack>
                      </Stack>
                    </FieldGroup>

                    {ShowLinkUrl ? (
                      <FieldGroup label="링크">
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="NoticeBoard 또는 www.naver.com"
                          value={Form.linkUrl ?? ''}
                          onChange={(e) =>
                            props.SetForm((prev) => ({ ...prev, linkUrl: e.target.value }))
                          }
                        />
                        <Typography
                          component="p"
                          sx={{
                            mt: 0.75,
                            mb: 0,
                            fontSize: '0.75rem',
                            color: '#64748b',
                            lineHeight: 1.55,
                          }}
                        >
                          메뉴 이동: 페이지명만 입력 (예: NoticeBoard, Home)
                          <br />
                          외부 사이트: 주소 입력 (예: www.naver.com, https://www.naver.com)
                        </Typography>
                      </FieldGroup>
                    ) : null}

                    <FieldGroup label="옵션">
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        alignItems="center"
                        useFlexGap
                        spacing={0.5}
                      >
                        <Checkbox
                          size="small"
                          name="사용"
                          checked={UseYnChecked}
                          nameSx={OPTION_CHECKBOX_SX}
                          onChange={(checked) =>
                            props.SetForm((prev) => ({
                              ...prev,
                              useYn: checked ? 'Y' : 'N',
                            }))
                          }
                        />
                        <Checkbox
                          size="small"
                          name="오늘 하루 보지 않기"
                          checked={IsHideTodayChecked}
                          disabled={IsForceConfirm}
                          nameSx={OPTION_CHECKBOX_SX}
                          onChange={(checked) =>
                            props.SetForm((prev) => ({
                              ...prev,
                              isHideToday: checked ? 'Y' : 'N',
                            }))
                          }
                        />
                        <Checkbox
                          size="small"
                          name="강제 확인 (동의 후 닫기)"
                          checked={IsForceCloseChecked}
                          disabled={IsForceConfirm}
                          nameSx={OPTION_CHECKBOX_SX}
                          onChange={(checked) =>
                            props.SetForm((prev) => ({
                              ...prev,
                              isForceClose: checked ? 'Y' : 'N',
                            }))
                          }
                        />
                      </Stack>
                    </FieldGroup>
                  </Stack>
                </WriteSection>

                {/* 2. 제목·내용 */}
                <WriteSection title="팝업 내용">
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      required
                      label="제목"
                      placeholder="사용자에게 보이는 제목"
                      size="small"
                      value={Form.title ?? ''}
                      onChange={(e) =>
                        props.SetForm((prev) => ({ ...prev, title: e.target.value }))
                      }
                      InputProps={{ sx: { fontWeight: 600 } }}
                    />
                    <Box
                      sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                      }}
                    >
                      <PopBasicEditor
                        value={Form.content ?? ''}
                        onChange={(content) =>
                          props.SetForm((prev) => ({ ...prev, content }))
                        }
                        onImageUpload={props.HandleImageUpload}
                      />
                    </Box>
                  </Stack>
                </WriteSection>

                {/* 4. 관리 메모 */}
                <WriteSection title="관리 메모 (선택)">
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    placeholder="내부 참고용 메모"
                    value={Form.adminMemo ?? ''}
                    onChange={(e) =>
                      props.SetForm((prev) => ({ ...prev, adminMemo: e.target.value }))
                    }
                  />
                </WriteSection>

                {IsDetail ? (
                  <Typography variant="caption" sx={{ color: '#94a3b8', px: 0.5 }}>
                    등록: {Form.createId || '-'} ·{' '}
                    {String(Form.createDate || '').substring(0, 16) || '-'}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export default POP_BASIC;
